import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { trackShare } from 'lib/Tracking';
import Markdown from 'components/ContentfulMarkdown';
import Sharing from 'components/Sharing';
import RefreshingAdhesionAd from 'components/Ad/RefreshingAdhesionAd';
import { colors, screen } from 'components/styles/variables';
import { CtaButton } from 'components/Shared/CtaButton';
import Link from 'next/link';
import { ArticleInterface } from 'types/Apis/ArticleInterface';
import SidebarAds from 'components/Ad/SidebarAds';
import { SourcePages } from 'lib/EventSchema';
import ContentModal, { CONTENT_MODAL_SHOWN_KEY } from 'components/ContentModal';
import { useState, useEffect } from 'react';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { getFullDocumentHeight } from 'lib/Utilities';
import RelatedArticles from './RelatedArticles';
import Category from './Category';

// styles
import styles from './styles';

export interface HeaderProps {
  heroImageURL: string;
  thumbImageURL: string;
}

const Header = ({ heroImageURL, thumbImageURL }: HeaderProps): JSX.Element => {
  return (
    <div className="hero">
      <style jsx>
        {`
          .hero {
            background-color: ${colors.G4};
            position: relative;
            padding: 0;
            background-image: url(${heroImageURL});
            background-size: cover;
            background-repeat: no-repeat;
          }
          @media (min-width: ${screen.mdMin}) {
            .hero {
              height: 400px;
            }
          }

          @media (max-width: ${screen.smMax}) {
            .hero {
              height: 350px;
            }
          }

          @media (max-width: ${screen.xsMax}) {
            .hero {
              height: 40vw;
            }
          }

          @media (max-width: ${screen.mobileMax}) {
            .hero {
              background-image: url(${thumbImageURL});
            }
          }
        `}
      </style>
    </div>
  );
};

function Article({
  article,
  shareUrl,
  related,
  includeAds = true,
  includeRelated = true,
}: {
  article?: ArticleInterface;
  related?: ArticleInterface[];
  shareUrl?: string;
  includeAds?: boolean;
  includeRelated?: boolean;
}): JSX.Element {
  const {
    title,
    body,
    category,
    parent_page: parentPage,
    slug,
    hero_image: heroImage,
    thumb_image: thumbImage,
    publication_date: publicationDate,
    ctaText,
    ctaURL,
  } = article || {};

  const [showModal, setShowModal] = useState(false);
  const [hasModalBeenShown, setHasModalBeenShown] = useState(false);

  useScrollPosition(
    ({ currPos }) => {
      const show = currPos.y < -(getFullDocumentHeight() / 4);
      if (!hasModalBeenShown && show) {
        setShowModal(true);
        setHasModalBeenShown(true);
        window.localStorage.setItem(CONTENT_MODAL_SHOWN_KEY, 'true');
      }
    },
    [showModal]
  );

  const bodyStateClass = heroImage ? '--with-hero' : '';

  const date = publicationDate ? new Date(publicationDate) : new Date(Date.now());

  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const authorName = get(article, 'author.name');
  const authorBylineUrl = get(article, 'author.bylineurl');

  const twitterShareText = `${title} ${shareUrl} via @autolistcom`;

  const { t } = useTranslation();

  const sharing = shareUrl && title && (
    <Sharing
      url={shareUrl}
      emailSubject={title}
      emailBody={`${t('article.emailShare')} ${shareUrl}`}
      twitterText={twitterShareText}
      onShare={(platform): void =>
        trackShare({
          category: 'srp_interaction',
          type: 'article',
          platform,
          url: shareUrl,
        })
      }
    />
  );

  const pageName = parentPage ? `/${parentPage.slug}/${slug}` : slug;
  const [contentModalShown, setContentModalShown] = useState<boolean>(false);

  useEffect(() => {
    setContentModalShown(window.localStorage.getItem(CONTENT_MODAL_SHOWN_KEY) === 'true');
  }, []);

  return (
    <div className="article clearfix container">
      {!contentModalShown && <ContentModal showModal={showModal} />}
      {includeAds && <RefreshingAdhesionAd visibilityBreakpoint={115} targeting={8} />}
      {heroImage && thumbImage && <Header heroImageURL={heroImage} thumbImageURL={thumbImage} />}
      <div className="below-hero">
        <div className={`body ${bodyStateClass}`}>
          <div className="clearfix">
            <Category category={category} />
            <div className="sharing --top pull-right">{sharing}</div>
          </div>
          <h1 className="title">{title}</h1>
          {authorName && (
            <div className="author-container">
              <span>By </span>
              <span>
                <Link href={`/news-and-analysis${authorBylineUrl}`}>
                  <a className="author-link">{authorName}</a>
                </Link>
              </span>
              <span> | {formattedDate}</span>
            </div>
          )}
          <div className="breaker" />
          <Markdown source={body} className="content" />
          <div className="cta">
            {ctaText && ctaURL && pageName && category && (
              <CtaButton
                ctaText={ctaText}
                ctaURL={ctaURL}
                category={category}
                pageName={pageName}
              />
            )}
          </div>
          <div className="sharing --bottom">{sharing}</div>
          {includeRelated && related && pageName && (
            <RelatedArticles
              eventsData={{
                categoryName: (category ? category.name : '') as SourcePages,
                pageName,
              }}
              parentCategoryName={get(category, 'parentCategory.name')}
              related={related}
            />
          )}
        </div>
        {includeAds && <SidebarAds sticky={true} />}
      </div>
      <style jsx>{styles}</style>
    </div>
  );
}

export default Article;
